import { US_STATES } from "@/shared/constants/constants";
import { fetchPlaceOfDeath } from "@/shared/hooks/usePlaceOfDeath";
import { supabase } from "@/shared/lib/supabase";
import { toStateAbbreviation } from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { sentryUtils } from "@/shared/utils/sentry";

import type { CreateEstateItemRequest, EstateItem } from "@/shared/types/estateTypes";

import { buildVehicleMetadataFromForm } from "./vehicleUtils";

/** Returns false when the link could not be saved, so the caller keeps the answer elsewhere. */
async function linkPlaceOfDeathState(estateId: number, state: string): Promise<boolean> {
  try {
    const current = await fetchPlaceOfDeath(estateId);
    if (toStateAbbreviation(current?.state ?? "") === state) return true;
    const stateName = US_STATES.find((option) => option.value === state)?.label ?? state;
    const result = await resolveAddressLink({
      raw: `${stateName}, USA`,
      purpose: "place_of_death",
      entity: { estate_id: estateId },
    });
    return !!result?.addressId;
  } catch (err) {
    sentryUtils.captureError(err, "Linking the place of death state failed", { estateId });
    return false;
  }
}

export async function saveBenefitsData(
  data: Record<string, string>,
  deceasedUserId: number,
  saveDeceasedSSN: (params: { ssn: string; estateId: number }) => Promise<unknown>,
) {
  const personUpdate: Record<string, unknown> = {};

  if (data.place_of_death && !(await linkPlaceOfDeathState(deceasedUserId, data.place_of_death))) {
    personUpdate.place_of_death = data.place_of_death;
  }
  if (data.funeral_home_reported_death) {
    personUpdate.funeral_home_reported = data.funeral_home_reported_death === "yes";
  }
  if (data.marriage_date || data.marriage_place) {
    const { data: current } = await supabase
      .from("estates")
      .select("marriage_info")
      .eq("estate_id", deceasedUserId)
      .single();
    const existing = (current?.marriage_info as Record<string, unknown>) ?? {};
    personUpdate.marriage_info = {
      ...existing,
      marriage_date: data.marriage_date || "",
      place: data.marriage_place || "",
    };
  }

  if (Object.keys(personUpdate).length > 0) {
    await supabase.from("estates").update(personUpdate).eq("estate_id", deceasedUserId);
  }

  const deceasedSSNDigits = data.deceased_ss_number?.replace(/\D/g, "") ?? "";
  if (deceasedSSNDigits.length === 9) {
    await saveDeceasedSSN({ ssn: deceasedSSNDigits, estateId: deceasedUserId });
  }
}

export async function saveMilitaryDetails(data: Record<string, string>, estateId: number) {
  const militaryDetails = JSON.stringify({
    branch: data.branch_of_service || "",
    startYear: data.service_start_date || "",
    endYear: data.service_end_date || "",
    dischargeStatus: data.discharge_status || "",
  });

  const { data: existingRows } = await supabase
    .from("estate_tasks")
    .select("answers")
    .eq("estate_id", estateId)
    .eq("task_id", "military")
    .limit(1);

  const existingAnswers = (existingRows?.[0]?.answers as Record<string, unknown> | null) ?? {};

  await supabase
    .from("estate_tasks")
    .update({
      answers: { ...existingAnswers, military_details: militaryDetails },
    })
    .eq("estate_id", estateId)
    .eq("task_id", "military");
}

export async function persistVehicleItems(
  data: Record<string, string>,
  vehicleItems: EstateItem[],
  estateId: number,
  addEstateItem: (item: CreateEstateItemRequest) => Promise<unknown>,
  updateEstateItem: (id: number, updates: Partial<CreateEstateItemRequest>) => Promise<unknown>,
) {
  const buildItemName = (prefix: string, fallback: string) => {
    const makeModel = data[`${prefix}vehicle_make_model`] ?? "";
    const year = data[`${prefix}vehicle_year`] ?? "";
    return [year, makeModel].filter(Boolean).join(" ") || fallback;
  };

  if (vehicleItems.length > 0) {
    const selectedId = data.selected_vehicle_id ?? "";
    const isNewVehicle = selectedId.startsWith("new-");

    if (isNewVehicle) {
      const newIndex = parseInt(selectedId.replace("new-", ""), 10);
      const prefix = `new${newIndex}_`;
      await addEstateItem({
        estate_id: estateId,
        category: "Vehicles",
        item_name: buildItemName(prefix, `Vehicle ${vehicleItems.length + newIndex + 1}`),
        item_type: "Vehicle",
        balance: 0,
        classification: "asset",
        metadata: buildVehicleMetadataFromForm(data, prefix),
      });
    } else {
      const numId = Number(selectedId) || vehicleItems[0].id;
      const target = vehicleItems.find((v) => v.id === numId) ?? vehicleItems[0];
      const existing = (target.metadata ?? {}) as Record<string, unknown>;
      const prefix = `est${target.id}_`;
      await updateEstateItem(target.id, {
        metadata: buildVehicleMetadataFromForm(data, prefix, existing),
      });
    }
  } else {
    const count = Math.max(1, parseInt(data.vehicle_count ?? "1", 10));
    for (let i = 0; i < count; i++) {
      const prefix = i === 0 ? "" : `v${i}_`;
      await addEstateItem({
        estate_id: estateId,
        category: "Vehicles",
        item_name: buildItemName(prefix, `Vehicle ${i + 1}`),
        item_type: "Vehicle",
        balance: 0,
        classification: "asset",
        metadata: buildVehicleMetadataFromForm(data, prefix),
      });
    }
  }
}
